import { API_ERROR_CODES } from '@ruta/shared';
import { ApiError, makeApiCall } from '../core';
import type {
    Crew,
    CrewMembersResponse,
    CrewMergePreview,
    CrewMergeResult,
    CrewTimeClockStatusResponse,
    ItemEnvelope,
    ListEnvelope,
} from '@ruta/shared';

// Legacy types still used in webapp (to be migrated)
import type {
    AssignMemberToCrewRequest,
    CreateCrewData,
    CrewDailySchedule,
    CrewJob,
    CrewLocationHistoryData,
    CrewLocationInfo,
    SearchUser,
    UpdateCrewData,
    UpdateCrewMemberRoleRequest,
} from '../types';

// =====================
// Crew Management API
// =====================

/**
 * The most customers one request may ask for a suggestion about. Mirrors the
 * server's own ceiling — see `MAX_RECOMMEND_FOR_CUSTOMERS` in `getCrews.ts`.
 *
 * Anything beyond it is TRUNCATED here, silently, exactly as the server does —
 * so a caller with more customers than this must chunk rather than rely on one
 * call, or the overflow simply gets no suggestion with nothing to say why.
 * `useConfigCrewSuggestions` is the worked example.
 */
export const MAX_RECOMMEND_FOR_CUSTOMERS = 100;

/**
 * Crews for the caller's org.
 *
 * Pass `recommendFor` to also learn which crews already work each of those
 * customers' neighbourhoods for one service (RUT-3316). Crews that are a
 * plausible geographic fit carry a `recommendations` entry per customer; the
 * rest carry nothing, because an admin can always pick any crew regardless. It
 * stays a read either way — it never assigns anyone.
 *
 * Several customers go in ONE request on purpose. The suggestion is derived from
 * a service's customer geography, so asking per customer would re-read and
 * re-cluster the same thing for every row of a table.
 */
export const getCrews = async (options?: {
  recommendFor?: { customerIds: string[]; serviceTemplateId: string };
}): Promise<{
  crews: Crew[];
  count: number;
}> => {
  const recommendFor = options?.recommendFor;
  const query =
    recommendFor && recommendFor.customerIds.length > 0
      ? `?${new URLSearchParams({
          recommendForCustomerIds: recommendFor.customerIds
            .slice(0, MAX_RECOMMEND_FOR_CUSTOMERS)
            .join(','),
          recommendForServiceTemplateId: recommendFor.serviceTemplateId,
        })}`
      : '';
  const response = await makeApiCall<ListEnvelope<Crew>>('GET', `/crews${query}`);
  return {
    crews: response.data,
    count: response.data.length,
  };
};

export const createCrew = async (
  crewData: CreateCrewData,
): Promise<Crew> => {
  const response = await makeApiCall<ItemEnvelope<Crew>>('POST', '/crews', crewData);
  return response.data;
};

export const updateCrew = async (
  crewId: string,
  crewData: UpdateCrewData,
): Promise<Crew> => {
  const response = await makeApiCall<ItemEnvelope<Crew>>('PATCH', `/crews/${crewId}`, crewData);
  return response.data;
};

/**
 * Preview a crew merge (RUT-3315) — how many services, visits and members sit on
 * each crew that would be merged away. Writes nothing.
 *
 * Goes through the existing PATCH /crews/{id} endpoint (no dedicated merge
 * route); the crew in the path is the one that survives.
 */
export const previewCrewMerge = async (
  survivingCrewId: string,
  mergeCrewIds: string[],
): Promise<CrewMergePreview> => {
  const response = await makeApiCall<ItemEnvelope<{ merge: CrewMergePreview }>>(
    'PATCH',
    `/crews/${survivingCrewId}`,
    { mergeCrewIds, dryRun: true },
  );
  return response.data.merge;
};

/** Run the merge previewed above. */
export const mergeCrews = async (
  survivingCrewId: string,
  mergeCrewIds: string[],
): Promise<CrewMergeResult> => {
  // dryRun defaults to true server-side, so a real merge must say so explicitly.
  const response = await makeApiCall<ItemEnvelope<{ mergeResult: CrewMergeResult }>>(
    'PATCH',
    `/crews/${survivingCrewId}`,
    { mergeCrewIds, dryRun: false },
  );
  return response.data.mergeResult;
};

export const deleteCrew = async (
  crewId: string,
  reassignToCrewId?: string,
): Promise<void> => {
  const query = reassignToCrewId
    ? `?reassignToCrewId=${encodeURIComponent(reassignToCrewId)}`
    : '';
  await makeApiCall('DELETE', `/crews/${crewId}${query}`);
};

export const searchUsers = async (
  searchQuery: string,
): Promise<{
  users: SearchUser[];
  count: number;
}> => {
  // Org-scoped members list; filter client-side (API supports ?q= on list).
  const { getOrganizationMembers } = await import('./organizations');
  const data = await getOrganizationMembers();
  const q = searchQuery.toLowerCase();
  const users = (data.members ?? [])
    .filter((m: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string }) => {
      const name = [m.firstName, m.lastName].filter(Boolean).join(' ').toLowerCase();
      return (
        name.includes(q) ||
        (m.email?.toLowerCase().includes(q) ?? false) ||
        (m.phoneNumber?.includes(searchQuery) ?? false)
      );
    })
    .map((m: any) => ({
      id: m.id,
      firstName: m.firstName,
      lastName: m.lastName,
      email: m.email,
      phoneNumber: m.phoneNumber,
      status: m.status,
    })) as SearchUser[];
  return { users, count: users.length };
};

// =====================
// Crew Jobs and Scheduling
// =====================

export const getCrewDailyJobs = async (
  crewId: string,
  date: string,
): Promise<{
  jobs: CrewJob[];
  schedule: CrewDailySchedule;
}> => {
  return makeApiCall('GET', `/crews/${crewId}/daily-jobs?date=${date}`);
};

// =====================
// Crew Location Tracking
// =====================

export const getCrewLocationHistory = async (
  hoursBack: number,
  crewMemberId?: string,
): Promise<{
  historicalData: CrewLocationHistoryData[];
  crewMemberId?: string;
  timeRange: {
    startTime: string;
    endTime: string;
    hoursBack: number;
  };
}> => {
  const params = new URLSearchParams({
    hoursBack: hoursBack.toString(),
  });
  if (crewMemberId) {
    params.append('crewMemberId', crewMemberId);
  }

  return makeApiCall('GET', `/crews/location-history?${params}`);
};

export const getCrewLocations = async (
  crewMemberId?: string,
): Promise<{
  locations: CrewLocationInfo[];
  activeVisits: any[];
  lastUpdated: string;
  crewMemberId?: string;
}> => {
  // Use new locations endpoint
  const response = await makeApiCall('GET', '/locations/live');
  const liveData = response.data;

  // Transform response to match expected format
  const locations = (liveData?.locations || []).map((loc: any) => ({
    memberId: loc.memberId,  // Keep as memberId for color mapping
    crewMemberId: loc.memberId,
    crewMemberName: loc.memberName || 'Unknown',
    memberName: loc.memberName,
    latitude: loc.latitude,
    longitude: loc.longitude,
    coordinates: [loc.longitude, loc.latitude] as [number, number],  // [lng, lat] for Mapbox
    timestamp: loc.timestamp,
    accuracy: loc.accuracy,
    isClocked: loc.isClocked,
    visitId: loc.visitId,
    lastUpdated: loc.timestamp,
    crewId: loc.crewId || 'unknown',
    crewName: loc.crewName || 'No Crew',
    batteryLevel: loc.batteryLevel,
    speed: loc.speed,
    heading: loc.heading,
  }));

  // Filter by crew member if specified
  const filteredLocations = crewMemberId
    ? locations.filter((loc: CrewLocationInfo) => loc.crewMemberId === crewMemberId)
    : locations;

  return {
    locations: filteredLocations,
    activeVisits: liveData?.activeVisits || [],
    lastUpdated: new Date().toISOString(),
    crewMemberId,
  };
};


// =====================
// Crew Member Management API (Organization Member Integration)
// =====================

// Get crew members (from organization members with crew assignments)
export const getCrewMembers = async (
  crewId: string,
): Promise<CrewMembersResponse> => {
  const response = await makeApiCall<ItemEnvelope<CrewMembersResponse>>(
    'GET',
    `/crews/${crewId}/members`,
  );
  return response.data;
};

// Assign organization member to crew
export const assignMemberToCrew = async (
  crewId: string,
  assignData: AssignMemberToCrewRequest,
): Promise<{
  memberId: string;
  memberName: string;
  crewId: string;
  crewName: string;
  role: string;
  assignedAt: string;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    memberId: string;
    memberName: string;
    crewId: string;
    crewName: string;
    role: string;
    assignedAt: string;
  }>>('POST', `/crews/${crewId}/members`, assignData);
  return response.data;
};

export type RemoveMemberFromCrewOptions = {
  /**
   * When the member is mid-visit on this crew, whether to also end their
   * work session on that visit (RUT-2874). Omit to let the server return
   * 409 MEMBER_ON_ACTIVE_VISIT so the UI can ask.
   */
  removeFromActiveVisit?: boolean;
};

export type ActiveVisitDetails = {
  visitId: string;
  customerName: string;
  serviceName: string;
};

export function isMemberOnActiveVisitError(
  error: unknown,
): error is ApiError & { details: ActiveVisitDetails } {
  return error instanceof ApiError && error.code === API_ERROR_CODES.MEMBER_ON_ACTIVE_VISIT;
}

// Remove member from crew. Server returns 204 No Content.
// Throws ApiError with code MEMBER_ON_ACTIVE_VISIT (and visit details) when
// the member is actively working a visit for this crew and no decision was
// supplied — callers should prompt and retry with `removeFromActiveVisit`.
export const removeMemberFromCrew = async (
  crewId: string,
  memberId: string,
  options?: RemoveMemberFromCrewOptions,
): Promise<void> => {
  const qs =
    options?.removeFromActiveVisit === undefined
      ? ''
      : `?removeFromActiveVisit=${options.removeFromActiveVisit ? 'true' : 'false'}`;
  await makeApiCall('DELETE', `/crews/${crewId}/members/${memberId}${qs}`);
};

/**
 * Remove a member from a crew, prompting (via `window.confirm`) when they are
 * mid-visit so the caller can decide whether to end their visit session too.
 * Use from compact UIs; full-page flows can handle MEMBER_ON_ACTIVE_VISIT with
 * a custom dialog instead (RUT-2874).
 */
export const removeMemberFromCrewPromptingForActiveVisit = async (
  crewId: string,
  memberId: string,
  memberName: string,
): Promise<void> => {
  try {
    await removeMemberFromCrew(crewId, memberId);
  } catch (error) {
    if (!isMemberOnActiveVisitError(error)) throw error;
    const details = (error.details ?? {}) as Partial<ActiveVisitDetails>;
    const visitLabel = [details.serviceName, details.customerName].filter(Boolean).join(' at ');
    const message = visitLabel
      ? `${memberName} is working ${visitLabel}. Also remove them from the visit?\n\nTheir time stops now and they can no longer act on it. The visit continues for the rest of the crew.\n\nOK = remove from visit · Cancel = leave on visit`
      : `${memberName} is actively working a visit for this crew. Also remove them from the visit?\n\nTheir time stops now and they can no longer act on it. The visit continues for the rest of the crew.\n\nOK = remove from visit · Cancel = leave on visit`;
    // Cancel → leave on visit (crew removal still goes through either way).
    const removeFromVisit = window.confirm(message);
    await removeMemberFromCrew(crewId, memberId, { removeFromActiveVisit: removeFromVisit });
  }
};

// Update member's role in crew
export const updateCrewMemberRole = async (
  crewId: string,
  memberId: string,
  roleData: UpdateCrewMemberRoleRequest,
): Promise<{
  id: string;
  firstName?: string;
  lastName?: string;
  crewId: string;
  crewName: string;
  newRole: string;
  updatedAt: string;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    id: string;
    firstName?: string;
    lastName?: string;
    crewId: string;
    crewName: string;
    newRole: string;
    updatedAt: string;
  }>>('PATCH', `/crews/${crewId}/members/${memberId}`, roleData);
  return response.data;
};


// =====================
// Break Management API
// =====================

export const startBreak = async (
  breakData: {
    crewId: string;
    memberId: string;
    startTime: string;
    location?: { latitude: number; longitude: number };
  },
): Promise<{
  message: string;
  break: {
    id: string;
    crewId: string;
    memberId: string;
    startTime: string;
    status: 'active';
  };
}> => {
  return makeApiCall('POST', '/crews/breaks', breakData);
};

export const endBreak = async (
  breakId: string,
  endData: {
    endTime: string;
    location?: { latitude: number; longitude: number };
  },
): Promise<{
  message: string;
  break: {
    id: string;
    crewId: string;
    memberId: string;
    startTime: string;
    endTime: string;
    status: 'completed';
    duration: number;
  };
}> => {
  return makeApiCall('PATCH', `/crews/breaks/${breakId}`, endData);
};

// =====================
// Time Tracking API (RESTful Time Entries)
// =====================

export const clockIn = async (
  timeEntryData: {
    crewMemberId: string;
    crewId: string;
    startTime: string;
    location?: { latitude: number; longitude: number };
    notes?: string;
  },
): Promise<{
  message: string;
  timeEntry: {
    id: string;
    crewMemberId: string;
    crewId: string;
    startTime: string;
    status: 'active';
  };
}> => {
  return makeApiCall('POST', '/time-entries', timeEntryData);
};

export const clockOut = async (
  entryId: string,
  clockOutData: {
    endTime: string;
    location?: { latitude: number; longitude: number };
    notes?: string;
  },
): Promise<{
  message: string;
  timeEntry: {
    id: string;
    crewMemberId: string;
    crewId: string;
    startTime: string;
    endTime: string;
    status: 'completed';
    duration: number;
  };
}> => {
  return makeApiCall('PATCH', `/time-entries/${entryId}`, clockOutData);
};

// Admin clock out - clocks out a member without requiring location
// Uses the clock-in location as fallback on the server
export const adminClockOut = async (
  entryId: string,
  notes?: string,
): Promise<{
  message: string;
  clockEvent: {
    id: string;
    clockOutTime: string;
    shiftDuration: number;
  };
  status: string;
  shiftDuration: number;
  adminAction?: boolean;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    clockEvent: {
      id: string;
      clockOutTime: string;
      shiftDuration: number;
    };
    status: string;
    shiftDuration: number;
    adminAction?: boolean;
  }>>('PATCH', `/time-entries/${entryId}`, {
    status: 'completed',
    notes,
  });
  return response.data;
};

// Bulk admin clock out - clocks out several members in one action.
// Resolves each member's active entry server-side; uses the clock-in location
// as fallback. Returns a summary of cleared vs. skipped members.
export const bulkClockOut = async (
  memberIds: string[],
  notes?: string,
): Promise<{
  message: string;
  clearedCount: number;
  clearedMemberIds: string[];
  skipped: Array<{ memberId: string; reason: 'not-clocked-in' | 'error' }>;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    message: string;
    clearedCount: number;
    clearedMemberIds: string[];
    skipped: Array<{ memberId: string; reason: 'not-clocked-in' | 'error' }>;
  }>>('POST', '/time-entries/bulk-clock-out', { memberIds, notes });
  return response.data;
};

export const getTimeEntries = async (
  filters: {
    crewMemberId?: string;
    crewId?: string;
    startDate?: string;
    endDate?: string;
  } = {},
): Promise<{
  timeEntries: Array<{
    id: string;
    crewMemberId: string;
    crewId: string;
    startTime: string;
    endTime?: string;
    status: 'active' | 'completed';
    duration?: number;
    notes?: string;
  }>;
  totalHours: number;
  count: number;
}> => {
  const params = new URLSearchParams();
  if (filters.crewMemberId) params.append('crewMemberId', filters.crewMemberId);
  if (filters.crewId) params.append('crewId', filters.crewId);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  const response = await makeApiCall<ItemEnvelope<{
    timeEntries: Array<{
      id: string;
      crewMemberId: string;
      crewId: string;
      startTime: string;
      endTime?: string;
      status: 'active' | 'completed';
      duration?: number;
      notes?: string;
    }>;
    totalHours: number;
    count: number;
  }>>('GET', `/time-entries${params.toString() ? `?${params}` : ''}`);
  return response.data;
};

// =====================
// Time Clock History API (Admin access to member sessions)
// =====================

export const getMemberTimeClockHistory = async (
  memberId: string,
  options: {
    days?: number;
    limit?: number;
  } = {},
): Promise<CrewTimeClockStatusResponse> => {
  const params = new URLSearchParams();
  params.append('memberId', memberId);
  if (options.days) params.append('days', options.days.toString());
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await makeApiCall<ItemEnvelope<CrewTimeClockStatusResponse>>(
    'GET',
    `/time-entries?${params}`,
  );
  return response.data;
};

// =====================
// Crew Capacity API
// =====================

export interface CrewCapacityInfo {
  crewId: string;
  crewName: string;
  date: string;
  memberCount: number;
  totalCapacityHours: number;
  scheduledHours: number;
  availableHours: number;
  utilizationPercentage: number;
  scheduledVisits: Array<{
    visitId: string;
    customerName: string;
    serviceName: string;
    estimatedDuration: number;
    scheduledTime?: string;
  }>;
}

export interface CrewCapacityResponse {
  date: string;
  items: CrewCapacityInfo[];
  totalCount?: number;
  nextToken?: string;
  hasMore: boolean;
  pageSize: number;
  summary: {
    totalCrews: number;
    totalCapacityHours: number;
    totalScheduledHours: number;
    totalAvailableHours: number;
    averageUtilization: number;
    queryTimeMs: number;
  };
}

export const getCrewCapacity = async (
  date: string,
  crewId?: string,
): Promise<CrewCapacityResponse> => {
  const params = new URLSearchParams({ date });
  if (crewId) {
    params.append('crewId', crewId);
  }

  const response = await makeApiCall<ItemEnvelope<CrewCapacityResponse>>(
    'GET',
    `/crews/capacity?${params}`,
  );
  return response.data;
};

// =====================
// Crew Notifications API
// =====================

export interface SendCrewNotificationRequest {
  crewIds: string[];
  message: string;
  urgent?: boolean;
  recipientType?: 'all' | 'leaders';
}

export interface SendCrewNotificationResponse {
  notificationsSent: number;
  smsSent?: number;
  pushSent?: number;
  errors?: Array<{
    crewId: string;
    error: string;
  }>;
}

export const sendCrewNotification = async (
  requestData: SendCrewNotificationRequest,
): Promise<SendCrewNotificationResponse> => {
  const response = await makeApiCall<ItemEnvelope<SendCrewNotificationResponse>>(
    'POST',
    '/crews/notifications',
    requestData,
  );
  return response.data;
};
